// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { handleAPIError } from '@/api/error-utils';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  AccessTimeOutlined as AccessTimeIcon,
  AssignmentOutlined as AssignmentIcon,
  AttachmentOutlined as AttachmentIcon,
  MonetizationOnOutlined as MonetizationOnIcon,
  ReceiptOutlined as ReceiptIcon,
  StickyNote2Outlined,
  Warning as WarningIcon,
} from '@/constants/hardfin-standard-icons';
import {
  DeleteProjectRequestParams,
  deleteProject,
} from '@/project/api/delete-project';
import { Project } from '@/project/api/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  reason: z.string().min(1, 'A reason is required to delete this project'),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  project: Project;
  onClose: () => void;
}

/**
 * Dialog component for confirming project deletion
 * Displays project information and requires a reason before deletion
 */
export function DeleteProjectDialog(props: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showSnackbar, setShowSnackbar] = useState(false);

  const hasCosts = props.project.costCount > 0;
  const hasTimeActivities = props.project.timeActivityCount > 0;
  const hasInvoicedCharges = props.project.invoicedChargeCount > 0;
  const hasBlockingAssociations =
    hasCosts || hasTimeActivities || hasInvoicedCharges;

  const deleteProjectMutation = useMutation({
    mutationFn: (parameters: DeleteProjectRequestParams) =>
      deleteProject(parameters),
    onSuccess: () => {
      close();
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      void navigate({ to: '/projects' });
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);
      // 404: the project is already gone — close the dialog and send the
      // user back to the list so they aren't stranded on a stale detail
      // page. No snackbar — the navigation is its own feedback.
      if (apiError.httpStatusCode === 404) {
        close();
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        void navigate({ to: '/projects' });
        return;
      }
      // Everything else (5xx / unknown) keeps the dialog open so the user
      // can retry without losing their reason text. Snackbar above the
      // dialog explains that something went wrong.
      if (!apiError.sentToSentry) {
        Sentry.captureException(error);
      }
      setShowSnackbar(true);
    },
  });

  const { control, reset, handleSubmit, formState } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: '',
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    return deleteProjectMutation.mutate({
      ...data,
      projectKey: props.project.key,
    });
  };

  const close = () => {
    props.onClose();
    reset();
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
          sx: {
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 2,
          }}
        >
          Delete project
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          {!hasBlockingAssociations && (
            <Alert
              severity="warning"
              icon={<WarningIcon fontSize="medium" />}
              sx={{
                marginY: 3,
                alignItems: 'center',
              }}
            >
              <AlertTitle variant="h6">
                Are you sure you want to delete project{' '}
                <strong>{props.project.name}</strong>?
              </AlertTitle>
              <Typography variant="body1">
                The project and all associated data will be permanently removed.
              </Typography>
            </Alert>
          )}

          {hasBlockingAssociations && (
            <Alert
              severity="error"
              icon={<WarningIcon fontSize="medium" />}
              sx={{
                marginY: 3,
                alignItems: 'center',
              }}
            >
              <AlertTitle variant="h6">
                Cannot delete project because of associated data
              </AlertTitle>
              <Stack spacing={1}>
                {hasCosts && (
                  <Typography variant="body1">
                    This project has associated cost records that must be
                    unlinked or deleted before the project can be removed.
                  </Typography>
                )}
                {hasTimeActivities && (
                  <Typography variant="body1">
                    This project has associated time records that must be
                    unlinked or deleted before the project can be removed.
                  </Typography>
                )}
                {hasInvoicedCharges && (
                  <Typography variant="body1">
                    This project has{' '}
                    {formatNumber(props.project.invoicedChargeCount)} invoiced{' '}
                    {pluralize(props.project.invoicedChargeCount, {
                      singular: 'charge',
                      plural: 'charges',
                      includeCount: false,
                    })}{' '}
                    that cannot be deleted.
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ marginTop: 2, marginBottom: 1 }}
          >
            Project associations
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              backgroundColor: 'background.default',
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <AttachmentIcon color="action" />
                <Typography variant="body2">
                  <strong>
                    {formatNumber(
                      props.project.fileCount + props.project.urlLinkCount
                    )}
                  </strong>{' '}
                  {pluralize(
                    props.project.fileCount + props.project.urlLinkCount,
                    {
                      singular: 'attachment',
                      plural: 'files and links',
                      includeCount: false,
                    }
                  )}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <StickyNote2Outlined color="action" />
                <Typography variant="body2">
                  <strong>{formatNumber(props.project.noteCount)}</strong>{' '}
                  {pluralize(props.project.noteCount, {
                    singular: 'note',
                    plural: 'notes',
                    includeCount: false,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <MonetizationOnIcon color="action" />
                <Typography variant="body2">
                  <strong>{formatNumber(props.project.costCount)}</strong>{' '}
                  {pluralize(props.project.costCount, {
                    singular: 'cost',
                    plural: 'costs',
                    includeCount: false,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <AccessTimeIcon color="action" />
                <Typography variant="body2">
                  <strong>
                    {formatNumber(props.project.timeActivityCount)}
                  </strong>{' '}
                  {pluralize(props.project.timeActivityCount, {
                    singular: 'time record',
                    plural: 'time records',
                    includeCount: false,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <AssignmentIcon color="action" />
                <Typography variant="body2">
                  <strong>
                    {formatNumber(props.project.totalAssignmentSegmentCount)}
                  </strong>{' '}
                  {pluralize(props.project.totalAssignmentSegmentCount, {
                    singular: 'assignment',
                    plural: 'assignments',
                    includeCount: false,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <ReceiptIcon color="action" />
                <Typography variant="body2">
                  <strong>{formatNumber(props.project.chargeCount)}</strong>{' '}
                  {pluralize(props.project.chargeCount, {
                    singular: 'charge',
                    plural: 'charges',
                    includeCount: false,
                  })}
                  {props.project.invoicedChargeCount > 0 && (
                    <>
                      {' '}
                      ({formatNumber(props.project.invoicedChargeCount)}{' '}
                      invoiced)
                    </>
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Typography variant="subtitle1" gutterBottom sx={{ marginBottom: 1 }}>
            Deletion reason
          </Typography>

          <FormTextField
            control={control}
            name="reason"
            fullWidth={true}
            placeholder="Please provide a reason for deletion"
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
            marginTop={2}
          >
            Note: your audit log will show a record of the deleted project and
            its details.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button onClick={close} variant="outlined">
            No, cancel
          </Button>
          <Button
            type="submit"
            color="error"
            variant="contained"
            disabled={formState.isSubmitting || hasBlockingAssociations}
            sx={{ ml: 1.5 }}
          >
            Yes, delete permanently
          </Button>
        </DialogActions>
      </Dialog>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message="Failed to delete project"
      />
    </>
  );
}
